import { hideToast, showSuccessToast, showToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import type { IToast } from "app/components/React/Toast/types";
import { i18nFilter } from "app/filters/i18nFilter";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import { salesforceDomainService } from "app/pages/sales-intelligence/modules/crm-export/utils/exportDomainService";
import { getAccountsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";
import { getCompanyAsLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/companyAsLeadsFields";
import { getContactsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/contactsFields";
import { getLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/leadsFields";
import { selectWebsitesExportToCrmAs } from "app/pages/sales-intelligence/sub-modules/company-search/store/selectors";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import multiSelectorPanelTrackingService from "../../../../../services/tracking/createMultiSelectorPanelService";
import type { FetchDomainsFn, FetchDomainsParams } from "../../../components/types";
import {
    addNewSettingsExport,
    deleteSettingExport,
    exportDomainsToSFAsync,
    fetchSfExportSettingsAsync,
    revertSettingsExport,
    saveSFExportSettingsAsync,
    saveSfAllSettingsError,
    updateSfSettingsExport,
} from "../../../store/actions/export/salesforce";
import { CrmListObjectType } from "../../../types";
import { createExportSettingsDto, deleteSetting, enrichExportSettings, groupByRecordType } from "../../../utils/export";
import { convertIntegrationTypeForApiSave, updateSettings } from "../../../utils/utils";
import { managementActions } from "../../action-creators";
import { getPreparedExportSettings } from "../../action-utils";
import { selectCacheToken, selectCurrentExportTab } from "../../selectors/common";
import {
    makeSelectSfColumns,
    selectSfCurrentSettings,
    selectSfPrevSettingsByCurrentType,
    selectSfSettings,
} from "../../selectors/salesforce/exportSelectors";
import type { ExportName, ExportSetting, NewExportSetting, SalesforcePreDefinedType } from "../../type";
import { exportToCrmToastThunk } from "../common";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

export const fetchSfExportSettingsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(fetchSfExportSettingsAsync.request());
        try {
            const settings = await deps.si.api.salesForceExportSettings.fetchSfExportSettings(signal);
            const { accounts, contacts, leads, companyAsLeads } = groupByRecordType(settings);

            dispatch(
                fetchSfExportSettingsAsync.success({
                    accounts: enrichExportSettings(accounts, getAccountsFields()),
                    contacts: enrichExportSettings(contacts, getContactsFields("salesforce")),
                    leads: enrichExportSettings(leads, getLeadsFields()),
                    companyAsLeads: enrichExportSettings(companyAsLeads, getCompanyAsLeadsFields()),
                }),
            );
        } catch (error) {
            dispatch(fetchSfExportSettingsAsync.failure(error));
        }
    };

export const updateSalesForceExportSettingsThunk =
    (oldSetting: ExportSetting, newSetting: NewExportSetting, exportName: ExportName, isCrmEnrichmentEnabled = false) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const settings: ExportSetting[] = selectSfCurrentSettings(getState());
        const columns = makeSelectSfColumns(typeName)(getState());

        const updatedSettings = updateSettings({
            settings,
            oldSetting,
            exportName,
            newSetting,
            columns,
            isCrmEnrichmentEnabled,
        });
        dispatch(updateSfSettingsExport({ typeName, settings: updatedSettings }));
    };

const saveSalesforceExportSettingsThunk =
    (typeName: ExportName) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(saveSFExportSettingsAsync.request());

        try {
            const state = getState();
            const settings: ExportSetting[] = selectSfSettings(state)[typeName];

            const mappedSettings = createExportSettingsDto(settings);

            const exportName = convertIntegrationTypeForApiSave(typeName) as SalesforcePreDefinedType;

            const { value } = await deps.si.api.salesForceExportSettings.saveSfExportSettings(
                {
                    fields: mappedSettings,
                },
                exportName,
            );

            if (value) {
                dispatch(saveSFExportSettingsAsync.success({ typeName, settings }));
            } else {
                dispatch(saveSFExportSettingsAsync.failure("Couldn't export to SF"));
            }
            return value;
        } catch (error) {
            dispatch(saveSFExportSettingsAsync.failure(error));
            return false;
        }
    };

export const addSettingsSalesForceExportThunk = (settings: string[], exportName: ExportName) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const existingSettings = selectSfCurrentSettings(getState());
        const preparedSettings = getPreparedExportSettings({
            settings,
            exportName,
            integration: "salesforce",
            existingSettings,
        });

        dispatch(addNewSettingsExport({ typeName, settings: preparedSettings }));
    };
};

export const deleteSfSettingLocalThunk = (field: string) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const settings: ExportSetting[] = selectSfCurrentSettings(getState());
        const preparedSettings = settings.filter((setting) => setting.field !== field);

        dispatch(updateSfSettingsExport({ typeName, settings: preparedSettings }));
    };
};

const deleteSettingToastSFExportThunk = (settings: ExportSetting) => {
    return (dispatch: ThunkDispatchCommon) => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text: i18nFilter()("si.management.sf.settings_export.toast.delete.content", {
                        fieldName: settings.title,
                    }),
                }),
            ),
        );
    };
};

const exportingDomainsToastSFExportThunk = (toast: IToast) => {
    return (dispatch: ThunkDispatchCommon) => {
        const { getToastComponent } = salesforceDomainService;

        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));
    };
};

export const deleteSFExportSettingThunk = (settingToDelete: ExportSetting) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings: ExportSetting[] = selectSfCurrentSettings(getState());
        const typeName = selectCurrentExportTab(getState());

        const { updatedSettings, deletedSetting } = deleteSetting(settings, settingToDelete);
        if (deletedSetting) {
            dispatch(deleteSettingExport({ typeName, settings: updatedSettings }));
            dispatch(deleteSettingToastSFExportThunk(deletedSetting));
        }
    };
};

const exportDomainsToSFWithNotificationThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId: string | undefined,
    toast: IToast,
) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        const { getErrorToastTitle, getSuccessToastTitle } = salesforceDomainService;
        const exportToCrmAsState = selectWebsitesExportToCrmAs(getState());

        try {
            const result =
                exportToCrmAsState === CrmListObjectType.Leads
                    ? await deps.si.api.salesForceExportSettings.exportDomainsAsLeadToSF(domains, countries, leadsType)
                    : await deps.si.api.salesForceExportSettings.exportDomainsToSF(
                          domains,
                          countries,
                          leadsType,
                          listId,
                      );
            if (result) {
                const { processedRecords, exportHistoryUrl } = result;
                dispatch(exportDomainsToSFAsync.success());
                dispatch(hideToast(toast));
                dispatch(
                    exportToCrmToastThunk(getSuccessToastTitle(processedRecords, domains.length), exportHistoryUrl),
                );
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.salesforce,
                    "success",
                    domains.length,
                    processedRecords,
                );
            } else {
                dispatch(hideToast(toast));
                dispatch(exportToCrmToastThunk(getErrorToastTitle()));
                dispatch(exportDomainsToSFAsync.failure("Couldn't export to SF"));
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.salesforce,
                    "fail",
                    domains.length,
                    0,
                );
            }
        } catch (error) {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToSFAsync.failure(error));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
            multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                ExportIntegrations.salesforce,
                "fail",
                domains.length,
                0,
            );
        }
    };
};

export const revertSettingsThunk = () => {
    return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings = selectSfPrevSettingsByCurrentType(getState());
        const typeName = selectCurrentExportTab(getState());

        dispatch(revertSettingsExport({ typeName, settings }));
    };
};

export const createExportTopNDomainsToSfThunk =
    <T extends FetchDomainsParams>(fetchDomainsThunk: FetchDomainsFn<T>) =>
    (params: T) =>
    async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToSFAsync.request());
        const { createToast, getErrorToastTitle, getExportingToastTitle } = salesforceDomainService;

        const toast = createToast(getExportingToastTitle(params.domainsCount));

        dispatch(exportingDomainsToastSFExportThunk(toast));

        const domains = await fetchDomainsThunk(params);

        if (domains?.length) {
            await dispatch(
                exportDomainsToSFWithNotificationThunk(
                    domains,
                    params.countries,
                    params.leadsType,
                    params.listParams?.id,
                    toast,
                ),
            );
        } else {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToSFAsync.failure("Couldn't prepare domains to export to SF"));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
        }
    };

export const exportDomainsToSFThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId?: string,
) => {
    return async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToSFAsync.request());
        const { createToast, getExportingToastTitle } = salesforceDomainService;

        const toast = createToast(getExportingToastTitle(domains.length));

        dispatch(exportingDomainsToastSFExportThunk(toast));
        await dispatch(exportDomainsToSFWithNotificationThunk(domains, countries, leadsType, listId, toast));
    };
};

export const getSfColumnsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(managementActions.salesforce.getSfColumnsAsync.request());

        const cacheToken = selectCacheToken(getState());
        try {
            const [accounts, contacts, leads] = await Promise.all([
                deps.si.api.management.getSfColumns("account", cacheToken, signal),
                deps.si.api.management.getSfColumns("contact", cacheToken, signal),
                deps.si.api.management.getSfColumns("lead", cacheToken, signal),
            ]);

            dispatch(
                managementActions.salesforce.getSfColumnsAsync.success({
                    accounts,
                    contacts,
                    leads,
                    companyAsLeads: leads,
                }),
            );
        } catch (error) {
            dispatch(managementActions.salesforce.getSfColumnsAsync.failure(error));
        }
    };

export const saveSfAllSettingsThunk =
    (changedTabs: ExportName[]) =>
    async (dispatch: ThunkDispatchCommon): Promise<{ successfullySavedSettings: ExportName[] } | undefined> => {
        try {
            const saveSettingPromises = changedTabs.map((tabName) => {
                return dispatch(saveSalesforceExportSettingsThunk(tabName)).then(
                    (isSuccess) => [tabName, isSuccess] as [ExportName, boolean],
                );
            });
            const saveSettingResults = await Promise.all(saveSettingPromises);

            const successfullySavedSettings = saveSettingResults
                .filter(([_, isSuccess]) => isSuccess)
                .map(([exportName]) => exportName);

            return { successfullySavedSettings };
        } catch (error) {
            dispatch(saveSfAllSettingsError(error));
        }
    };
